import { useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useCallback, useRef } from 'react';

import { useSnackbar } from '@shared/hooks/useSnackbar';

export type Handle404ErrorOptions = {
  message: string;
  redirectUrl?: string;
  onError?: () => void;
};

export const isAxios404Error = (error: unknown): error is AxiosError =>
  error instanceof AxiosError && (error.status === 404 || error.response?.status === 404);

/**
 * Returns a callback function that handles 404 errors (other than Site Not Found 404 errors) consistently
 * Used by both Page404ErrorBoundary (for suspense queries) and useHandle404Error (for regular queries).
 */
export const use404ErrorCallback = ({ message, redirectUrl, onError }: Handle404ErrorOptions) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const hasHandledError = useRef(false);

  return useCallback(
    (error: unknown) => {
      if (!isAxios404Error(error) || hasHandledError.current) {
        return;
      }

      hasHandledError.current = true;

      enqueueSnackbar(message, { variant: 'error' });

      if (onError) {
        onError();
      }

      if (redirectUrl) {
        navigate({ to: redirectUrl });
      }
    },
    [message, redirectUrl, onError, navigate, enqueueSnackbar]
  );
};
